import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { RankRewards } from '#src/components/leaderboard/RankRewards/index.tsx';
import { ScoreRewards } from '#src/components/leaderboard/ScoreRewards/index.tsx';
import { ThresholdRewards } from '#src/components/leaderboard/ThresholdRewards/index.tsx';
import { GenericLeaderboardPrizePoolLayout } from '#src/layouts/GenericLeaderboardPrizePoolLayout/index.tsx';

import type {
  PrizePoolContenderPageQuery,
  PrizePoolContenderPageQueryVariables,
} from './__generated__/index.graphql.ts';

const PRIZE_POOL_CONTENDER_PAGE_QUERY = gql`
  query PrizePoolContenderPageQuery(
    $leaderboard: String!
    $isContender: Boolean!
    $contender: String!
  ) {
    so5 {
      so5Leaderboard(slug: $leaderboard) {
        slug
        so5League {
          slug
          ...ScoreRewards_so5League
          ...ThresholdRewards_so5League
        }
        rewardsConfig {
          conditional {
            id
            ...ThresholdRewards_so5RewardConfig
          }
          targetable {
            id
            ...ScoreRewards_so5RewardConfig
          }
        }
        ...RankRewards_so5Leaderboard
        ...GenericLeaderboardPrizePoolLayout_so5Leaderboard
      }
      so5LeaderboardContender(slug: $contender) @include(if: $isContender) {
        slug
        so5Lineup {
          id
          so5Rankings {
            id
            ...RankRewards_so5Ranking
            ...ScoreRewards_so5Ranking
          }
        }
        ...GenericLeaderboardPrizePoolLayout_so5LeaderboardContender
      }
    }
  }
  ${ThresholdRewards.fragments.so5League}
  ${ThresholdRewards.fragments.so5RewardConfig}
  ${GenericLeaderboardPrizePoolLayout.fragments.so5Leaderboard}
  ${GenericLeaderboardPrizePoolLayout.fragments.so5LeaderboardContender}
  ${RankRewards.fragments.so5Leaderboard}
  ${RankRewards.fragments.so5Ranking}
  ${ScoreRewards.fragments.so5League}
  ${ScoreRewards.fragments.so5Ranking}
  ${ScoreRewards.fragments.so5RewardConfig}
` as TypedDocumentNode<
  PrizePoolContenderPageQuery,
  PrizePoolContenderPageQueryVariables
>;

type Props = {
  leaderboard: string;
  contender?: string;
};

export const PrizePoolContenderPage = ({
  leaderboard,
  contender = '',
}: Props) => {
  const { data } = useQuery(PRIZE_POOL_CONTENDER_PAGE_QUERY, {
    variables: {
      leaderboard,
      contender,
      isContender: !!contender,
    },
  });
  const openDialog = useOpenDialog();
  const { so5Leaderboard, so5LeaderboardContender } = data?.so5 || {};

  if (!so5Leaderboard) {
    return null;
  }

  const contenderInLeaderboard =
    so5LeaderboardContender?.so5Leaderboard.slug === so5Leaderboard.slug
      ? so5LeaderboardContender
      : undefined;

  const hasRankRewards = !!(so5Leaderboard.rewardsConfig.ranking || []).length;
  const hasScoreRewards = !!(so5Leaderboard.rewardsConfig.conditional || [])
    .length;
  const hasTargetRewards = !!(so5Leaderboard.rewardsConfig.targetable || [])
    .length;

  const hasMultipleTypeRewards =
    [hasRankRewards, hasScoreRewards, hasTargetRewards].filter(Boolean).length >
    1;

  return (
    <GenericLeaderboardPrizePoolLayout
      so5Leaderboard={so5Leaderboard}
      so5LeaderboardContender={so5LeaderboardContender || undefined}
    >
      <Vertical gap={2}>
        {!!hasTargetRewards && (
          <>
            {!!hasMultipleTypeRewards && (
              <BodyS bold>
                <FormattedMessage {...fantasy.rewardsPerTarget} />
              </BodyS>
            )}
            <ScoreRewards
              targetable
              defaultRarity={so5Leaderboard.mainRarityType}
              so5RewardConfigs={so5Leaderboard.rewardsConfig.targetable || []}
              so5League={so5Leaderboard.so5League}
              so5Ranking={contenderInLeaderboard?.so5Lineup?.so5Rankings[0]}
            />
          </>
        )}
        {!!hasScoreRewards && !hasTargetRewards && (
          <ThresholdRewards
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.conditional || []}
            so5League={so5Leaderboard.so5League}
          />
        )}
        {!!hasRankRewards && (
          <>
            {!!hasMultipleTypeRewards && (
              <BodyS bold>
                <FormattedMessage {...fantasy.rewardsPerRank} />
              </BodyS>
            )}
            <RankRewards
              so5Leaderboard={so5Leaderboard}
              so5Ranking={contenderInLeaderboard?.so5Lineup?.so5Rankings[0]}
              onClickRanking={value => openDialog(DialogKey.contender, value)}
            />
          </>
        )}
      </Vertical>
    </GenericLeaderboardPrizePoolLayout>
  );
};
